import { useState, useLayoutEffect, type RefObject } from 'react';

/** Column count assumed before the first measurement — matches the CSS `var(--board-cols, 5)` fallback. */
const DEFAULT_COLUMNS = 5;

// Minimum thumbnail widths for the two board views — tune these to change how wide a board has to
// get before it adds another column. A thumbnail never renders narrower than its minimum; past the
// 5-column cap the extra width goes into tile size instead (the grid's `1fr` does that on its own).
export const BOARD_MIN_TILE_EXPANDED = 160;
export const BOARD_MIN_TILE_COMPACT = 180;

/**
 * How many columns of at-least-`min`px thumbnails fit in `usableWidth`, capped at `max`.
 * Grows the column count only up to `max` (beyond that a wider board enlarges the thumbnails
 * rather than adding more), and never drops below 1 or below the `min` thumbnail width.
 */
export function computeColumnCount(usableWidth: number, min: number, gap: number, max: number): number {
  if (usableWidth <= 0) return 1;
  const fit = Math.floor((usableWidth + gap) / (min + gap));
  return Math.max(1, Math.min(max, fit));
}

/**
 * Tracks the responsive column count (1..max) for `ref`'s content-box width, re-measuring on resize.
 * Content width = clientWidth minus horizontal padding, so it matches the inner grid area.
 */
export function useResponsiveColumns(
  ref: RefObject<HTMLElement | null>,
  opts?: { min?: number; gap?: number; max?: number },
): number {
  const min = opts?.min ?? BOARD_MIN_TILE_EXPANDED;
  const gap = opts?.gap ?? 10;
  const max = opts?.max ?? 5;
  const [cols, setCols] = useState(DEFAULT_COLUMNS);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const style = getComputedStyle(el);
      const padX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const usable = el.clientWidth - padX;
      // A native re-frame mid-rotation can report a transient zero width. Latching that would
      // collapse the board to a single full-width tile with nothing to trigger a recovery, so
      // hold the last good count instead.
      if (usable <= 0) return;
      setCols(computeColumnCount(usable, min, gap, max));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // Backstops for a ResizeObserver delivery the WebView drops across a rotation; the
    // orientation change lands before the new layout, so measure on the next frame.
    let raf = 0;
    const measureNextFrame = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measureNextFrame);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measureNextFrame);
    };
  }, [ref, min, gap, max]);

  return cols;
}
